import React, { useState } from 'react';
import { readDisplayPrefs, textSizePx } from './calendarPanes';
import { linkParts } from './notesText';
import { CloseX, useCloseGuard } from './calendarModal';

// =============================================================================
// 5.43.0 "⤢ Expand" — a full-page reader for an event's / task's notes (DESCRIPTION). Big text that follows the
// calendar text size on this computer (≈1.45× the Aa setting), line breaks kept, links clickable (as text nodes +
// <a>, never HTML), scrolls for very long notes; the title and its date / time (Central) at the top.
// canEdit (same rule as the window it came from): "✎ Edit" -> a big text box with Save / Cancel; Save hands the new
// text to onSave, which writes it the normal way (the same save path as the window). Otherwise Close only.
// Closes only by its buttons (Close / Cancel / Save / ✕); unsaved edits ask "Discard changes?".
// =============================================================================

export function ExpandButton({ onClick, testid = 'notes-expand', dark = false }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} title="Read the notes full-page"
      style={{ background: 'transparent', border: `1px solid ${dark ? 'rgba(255,255,255,0.45)' : '#4b4b4b'}`, color: dark ? '#fff' : '#bbb',
        borderRadius: 5, fontSize: 11, padding: '1px 6px', cursor: 'pointer', lineHeight: 1.4, whiteSpace: 'nowrap' }}>⤢ Expand</button>
  );
}

export default function NotesReader({ title, when, notes, canEdit, onSave, onClose, saveLabel = 'Save' }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(notes || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const dirty = editing && text !== (notes || '');
  const { requestClose, prompt } = useCloseGuard(dirty, onClose);
  const px = Math.round(textSizePx(readDisplayPrefs().size) * 1.45);

  const save = async () => {
    if (!dirty) { onClose(); return; }
    setBusy(true); setErr('');
    try {
      const ok = await onSave(text);
      if (ok !== false) onClose();
      else setErr('Not saved — the message behind this window says why');
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Could not save the notes');
    } finally { setBusy(false); }
  };

  const btn = (bg) => ({ padding: '9px 18px', border: 'none', borderRadius: 7, background: bg, color: '#fff', fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 });
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2400, background: '#141414', color: '#f0f0f0', display: 'flex', flexDirection: 'column' }}
      role="dialog" aria-label="Notes" data-testid="notes-reader">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 22px', borderBottom: '1px solid #333', background: '#1f1f1f' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: Math.round(px * 1.15), fontWeight: 700, wordBreak: 'break-word' }} data-testid="notes-reader-title">{title || '(no title)'}</div>
          {when && <div style={{ fontSize: Math.max(13, Math.round(px * 0.75)), color: '#aaa', marginTop: 4 }} data-testid="notes-reader-when">{when}</div>}
        </div>
        <CloseX onClick={requestClose} disabled={busy} testid="notes-reader-x" />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '22px 28px' }} data-testid="notes-reader-body">
        {editing ? (
          <textarea value={text} onChange={(e) => setText(e.target.value)} autoFocus data-testid="notes-reader-text"
            style={{ width: '100%', height: '100%', minHeight: 300, boxSizing: 'border-box', resize: 'none', background: '#1a1a1a', color: '#f0f0f0',
              border: '1px solid #3a3a3a', borderRadius: 8, padding: 14, fontSize: px, lineHeight: 1.5, fontFamily: 'inherit' }} />
        ) : (
          <div style={{ maxWidth: 1000, margin: '0 auto', fontSize: px, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} data-testid="notes-reader-view">
            {(notes || '') === '' ? <span style={{ color: '#888' }}>No notes.</span> : linkParts(notes).map((p, i) => (p.href
              ? <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" style={{ color: '#93c5fd' }}>{p.text}</a>
              : <React.Fragment key={i}>{p.text}</React.Fragment>))}
          </div>
        )}
      </div>
      {err && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '8px 22px', fontSize: 13 }} data-testid="notes-reader-error">{err}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '12px 22px', borderTop: '1px solid #333', background: '#1f1f1f' }}>
        {!editing && canEdit && <button type="button" style={btn('#334155')} onClick={() => setEditing(true)} data-testid="notes-reader-edit">✎ Edit</button>}
        {editing && <button type="button" style={btn('#7c3aed')} disabled={busy} onClick={save} data-testid="notes-reader-save">{busy ? 'Saving…' : saveLabel}</button>}
        {editing
          ? <button type="button" style={btn('#444')} disabled={busy} onClick={requestClose} data-testid="notes-reader-cancel">Cancel</button>
          : <button type="button" style={btn('#444')} onClick={onClose} data-testid="notes-reader-close">Close</button>}
      </div>
      {prompt}
    </div>
  );
}
